'use client';

import type {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';

import { cn } from '@polyseme/utils/cn/cn-utils';
import {
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import { Skeleton } from '../../ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { HeaderCell } from './header-cell';
import { Pagination } from './pagination';

const PAGINATION_PAGE_SIZE = 10;
const SKELETON_ROW_COUNT = 8;

export const ACTIONS_COLUMN_ID = 'actions';

/**
 * Props for the DataTable component.
 *
 * Generic, sortable, filterable, and paginated table built on TanStack Table.
 * Uses semantic HTML table elements. Rows expose `data-state="selected"` for styling.
 * Columns with id "actions" are rendered as sticky on the right.
 * Column definitions support `meta.headerClassName` for custom header styling.
 *
 * Supports both controlled and uncontrolled modes for pagination, sorting, and column filters.
 * When controlled props are provided, they take precedence over internal state.
 */
type BaseDataTableProps<TData> = {
  className?: string;
  /** TanStack Table column definitions. Supports `meta.headerClassName` for custom header styles. */
  columns: ColumnDef<TData>[];
  /** Array of row data. The generic type ensures type safety between data and column accessors. */
  data: TData[];
  emptyMessage?: string;
  /** Hides the pagination controls. Useful for small, non-paginated datasets. */
  hidePagination?: boolean;
  /**
   * When true, rows are already filtered upstream (e.g. server-side): the table
   * keeps every row it is given instead of applying `columnFilters` locally.
   * Column filter UI/state still works; use it to drive the server query.
   */
  manualFiltering?: boolean;
  /**
   * When true, rows are already paginated upstream: the table renders every row
   * it is given instead of slicing to `pageSize`. Pair with your own controls.
   */
  manualPagination?: boolean;
  /** Renders skeleton rows in place of the body while a fetch is in flight. */
  loading?: boolean;
  /** Initial pagination state (uncontrolled mode). @default { pageIndex: 0, pageSize: 10 } */
  initialPagination?: PaginationState;
  /** Initial sorting state (uncontrolled mode). */
  initialSorting?: SortingState;
  /** i18n labels for pagination buttons. */
  labels?: {
    next: string;
    previous: string;
  };
  /** Function to derive a unique row ID from row data. When provided, selection keys will be these IDs instead of array indices. */
  getRowId?: (row: TData) => string;
  /** Function returning extra classes for a row, derived from its data. Useful to visually distinguish rows (e.g. soft-deleted). */
  getRowClassName?: (row: TData) => string | undefined;
  /** Callback fired when row selection changes. */
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
};

type ControlledPagination =
  | {
      pagination: PaginationState;
      onPaginationChange: OnChangeFn<PaginationState>;
    }
  | { pagination?: never; onPaginationChange?: never };

type ControlledSorting =
  | { sorting: SortingState; onSortingChange: OnChangeFn<SortingState> }
  | { sorting?: never; onSortingChange?: never };

type ControlledColumnFilters =
  | {
      columnFilters: ColumnFiltersState;
      onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
    }
  | { columnFilters?: never; onColumnFiltersChange?: never };

type DataTableProps<TData> = BaseDataTableProps<TData> &
  ControlledColumnFilters &
  ControlledPagination &
  ControlledSorting;

export const DataTable = <TData,>({
  className,
  columns,
  data,
  emptyMessage = 'Aucune donnée',
  hidePagination = false,
  manualFiltering = false,
  manualPagination = false,
  loading = false,
  initialPagination = {
    pageIndex: 0,
    pageSize: PAGINATION_PAGE_SIZE,
  },
  initialSorting = [],
  labels = {
    next: 'Suivant',
    previous: 'Précédent',
  },
  getRowId,
  getRowClassName,
  onRowSelectionChange,
  pagination: controlledPagination,
  onPaginationChange: controlledOnPaginationChange,
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange: controlledOnColumnFiltersChange,
}: DataTableProps<TData>) => {
  const [internalPagination, setInternalPagination] =
    useState<PaginationState>(initialPagination);
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);
  const [internalSorting, setInternalSorting] =
    useState<SortingState>(initialSorting);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const effectivePagination = controlledPagination ?? internalPagination;
  const effectiveOnPaginationChange =
    controlledOnPaginationChange ?? setInternalPagination;
  const effectiveSorting = controlledSorting ?? internalSorting;
  const effectiveOnSortingChange =
    controlledOnSortingChange ?? setInternalSorting;
  const effectiveColumnFilters =
    controlledColumnFilters ?? internalColumnFilters;
  const effectiveOnColumnFiltersChange =
    controlledOnColumnFiltersChange ?? setInternalColumnFilters;

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    setRowSelection((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onRowSelectionChange?.(next);
      return next;
    });
  };

  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualFiltering,
    manualPagination,
    onColumnFiltersChange: effectiveOnColumnFiltersChange,
    onSortingChange: effectiveOnSortingChange,
    onPaginationChange: effectiveOnPaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
    state: {
      columnFilters: effectiveColumnFilters,
      sorting: effectiveSorting,
      pagination: effectivePagination,
      rowSelection,
    },
    autoResetPageIndex: controlledPagination === undefined && !manualPagination,
  });

  const totalPages = table.getPageCount();

  const skeletonRows = loading
    ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowKey) => (
        <TableRow
          key={`skeleton-${rowKey}`}
          className="flex w-full min-w-max items-stretch justify-between bg-table-background even:bg-table-alternate"
          data-testid={`skeleton-row-${rowKey}`}
        >
          {table.getVisibleFlatColumns().map((column, colIndex) => (
            <TableCell
              key={`skeleton-${column.id}-${colIndex}`}
              className="flex shrink-0 items-center bg-inherit"
              style={{ width: `${column.getSize()}px` }}
            >
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))
    : null;

  return (
    <>
      <div className={cn('rounded-md', className)}>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <TableRow
                key={`${headerGroup.id}-${index}`}
                className="flex w-full min-w-max items-center justify-between"
              >
                {headerGroup.headers.map((header, colIndex) => (
                  <TableHead
                    key={`${header.id}-${colIndex}`}
                    className={cn(
                      'flex shrink-0 items-center px-0',
                      header.column.columnDef.meta?.headerClassName,
                      header.column.id === ACTIONS_COLUMN_ID &&
                        'sticky right-0 z-10 border-l border-border bg-white'
                    )}
                    data-testid={`header-${header.id}`}
                    style={{ width: `${header.getSize()}px` }}
                  >
                    <HeaderCell header={header} />
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading && skeletonRows}
            {!loading &&
              (table.getRowModel().rows?.length
                ? table.getRowModel().rows.map((row, key) => (
                    <TableRow
                      key={`${row.id}-${key}`}
                      className={cn(
                        'flex w-full min-w-max items-stretch justify-between bg-table-background even:bg-table-alternate',
                        getRowClassName?.(row.original)
                      )}
                      data-state={row.getIsSelected() && 'selected'}
                      data-testid={`row-${key}`}
                    >
                      {row.getVisibleCells().map((cell, colIndex) => (
                        <TableCell
                          key={`${cell.id}-${colIndex}`}
                          className={cn(
                            'flex shrink-0 items-center bg-inherit',
                            cell.column.id === ACTIONS_COLUMN_ID &&
                              'sticky right-0 z-10 border-l border-border bg-inherit'
                          )}
                          data-testid={`cell-${cell.column.id}-${key}`}
                          style={{ width: `${cell.column.getSize()}px` }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : !!emptyMessage && (
                    <TableRow>
                      <TableCell
                        className="h-24 text-center"
                        colSpan={columns.length}
                      >
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
      </div>
      {!hidePagination && totalPages > 1 && (
        <div className="flex items-center justify-end space-x-2 py-4">
          <Pagination
            currentPage={table.getState().pagination.pageIndex}
            labels={labels}
            setPageIndex={table.setPageIndex}
            totalPages={totalPages}
          />
        </div>
      )}
    </>
  );
};
